import React from 'react';
import { Space } from 'antd';
import { Moment } from 'moment';
import {
	getUtcOffset,
	toMoment,
	nowMoment,
} from '@shared/utils/date';
import {
	ContractNamingConventions,
	Currencies,
	DATE_AND_TIME,
	FixtureTypes,
	UnnamedBankAccountName,
	VesselOwnershipTypes,
	TimeZoneFormat,
	TimeZoneMode,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { formatCurrency } from '@shared/utils/currency';
import {
	fromISOTimezone,
	timeZoneOffsetLabel,
	toISOTimezone,
} from '@shared/utils/timezones';
import type { GetFixtureDetailsResponse } from '@api/features/fixtures/getFixtureDetails';
import type { GetCounterpartiesResponse } from '@api/features/counterparties/getCounterparties';
import type { GetOrgBankAccountsResponse } from '@api/features/organizations/getOrgBankAccounts';
import type { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import type { TcFixtureProps } from '@api/models/tc-fixture';
import type { BbFixtureProps } from '@api/models/bb-fixture';
import type { FixingCompanyProps } from '@api/models/fixing-company';
import VesselDescription from '@client/components/VesselDescription';
import { renderUtcDate } from '@client/utils/table';
import TooltipIcon from '@client/components/TooltipIcon';
import LinkButton from '@client/components/LinkButton';
import { Links } from '@client/utils/links';
import getVesselOptions from '@client/utils/getVesselOptions';
import Button from '@client/components/Button';
import DatePicker from '@client/components/DatePicker';
import getFixingCompanyOptions from '@client/utils/getFixingCompanyOptions';
import styles from '../tc/styles/useTcSectionItems.module.css';

export type SectionItemsFixture = GetFixtureDetailsResponse<TcFixtureProps | BbFixtureProps>;
export type SectionItemsBankAccount = GetOrgBankAccountsResponse[number];
export type FirstDeliveryPort = TcTermsWithIncludes['deliveryPortsOrRanges'][number] | null;

export const getChartererField = ({
	fixture,
	required,
	charterers,
}: {
	fixture: SectionItemsFixture;
	required: boolean;
	charterers: GetCounterpartiesResponse | undefined;
}) => ({
	key: 'counterpartyId',
	placeholder: 'Select a counterparty',
	value: fixture.counterpartyId,
	name: 'counterpartyId',
	label: fixture.type === FixtureTypes.TC_IN ? 'Owner' : 'Charterer',
	render: () => (
		<LinkButton
			url={(fixture.type === FixtureTypes.TC_IN ?
				Links.Owner.get(fixture.counterpartyId as number) :
				Links.Charterer.get(fixture.counterpartyId as number)
			)}
		>
			{fixture.counterpartyName}
		</LinkButton>
	),
	editable: true,
	options: (charterers ?? []).map((c) => ({
		label: c.name,
		value: c.id,
	})),
	inputProps: {
		'data-testid': 'charterer-select',
		showSearch: true,
	},
	type: 'select' as const,
	required,
});

export const getFixingCompanyField = ({
	fixture,
	fixingCompanies,
}: {
	fixture: SectionItemsFixture;
	fixingCompanies: Array<FixingCompanyProps>;
}) => ({
	key: 'fixingCompanyId',
	placeholder: 'Select a Fixing Company',
	value: fixture.fixingCompanyId,
	name: 'fixingCompanyId',
	label: 'Fixing Company',
	render: () => fixingCompanies.find((c) => c.id === fixture.fixingCompanyId)?.name ?? '',
	editable: !fixture.fixed,
	options: getFixingCompanyOptions(fixingCompanies, fixture.fixingCompanyId),
	inputProps: {
		'data-testid': 'fixing-company-select',
		showSearch: true,
	},
	type: 'select' as const,
	required: true,
});

export const getIdentifierField = ({
	fixture,
	namingConvention,
	onRegenerate,
}: {
	fixture: SectionItemsFixture;
	namingConvention: Values<typeof ContractNamingConventions>;
	onRegenerate: () => void;
}) => ({
	key: 'identifier',
	label: 'Contract Identifier',
	value: fixture.identifier,
	editable: namingConvention === ContractNamingConventions.FREE,
	render: () => (fixture.identifier == null ? (
		<>
			—
			<TooltipIcon>
				The unique identifier is set when a
				vessel is selected for the contract
			</TooltipIcon>
		</>
	) : (
		<Space>
			{fixture.identifier}
			<Button onClick={onRegenerate} type="link">
				Regenerate
			</Button>
		</Space>
	)),
});

export const getVesselField = ({
	fixture,
	vessels,
	required,
}: {
	fixture: SectionItemsFixture;
	vessels: unknown[];
	required: boolean;
}) => ({
	key: 'vesselId',
	label: 'Vessel',
	value: fixture.vesselId,
	editable: !fixture.fixed,
	required,
	type: 'select' as const,
	inputProps: {
		showSearch: true,
	},
	render: () => (
		<LinkButton
			url={Links.Vessel.get(fixture.vesselId as number)}
		>
			<VesselDescription
				name={fixture.vessel.name}
				flag={fixture.vessel.flag}
			/>
		</LinkButton>
	),
	options: [
		{
			label: 'No vessel',
			value: null,
		},
		{
			label: 'My vessels',
			options: getVesselOptions(vessels, 'myVessels', fixture.type),
		},
		{
			label: 'Market vessels',
			options: getVesselOptions(vessels, 'marketVessels'),
		},
	],
});

const shouldIncludeVesselCosts = (
	localFixture: SectionItemsFixture,
) => {
	return !!(
		localFixture.vessel &&
		localFixture.bankAccount &&
		localFixture.bankAccount.currency &&
		localFixture.vessel.ownershipType === VesselOwnershipTypes.TC_IN
	);
};

export const getVesselCosts = ({ fixture }: { fixture: SectionItemsFixture }) => ({
	key: 'tcInRate',
	label: 'TC In hire cost',
	value: fixture.tcTerms.tcInRate,
	editable: !fixture.fixed,
	required: shouldIncludeVesselCosts(fixture),
	type: 'currency' as const,
	render: (i: { value: number | null }) => {
		return formatCurrency(i.value || 0, fixture.bankAccount ?
			fixture.bankAccount.currency : Currencies.USD);
	},
	inputProps: {
		currency: shouldIncludeVesselCosts(fixture) ? fixture.bankAccount.currency : Currencies.USD,
		addonAfter: '/ day',
		disabled: !shouldIncludeVesselCosts(fixture),
	},
});

export const getBankAccountField = ({
	fixture,
	bankAccounts,
	required,
}: {
	fixture: SectionItemsFixture;
	bankAccounts: SectionItemsBankAccount[];
	required: boolean;
}) => ({
	key: 'bankAccountId',
	label: 'Bank Account & Currency',
	value: fixture.bankAccountId,
	editable: (bankAccounts.length > 0),
	inputProps: {
		'data-testid': 'bank-account',
		placeholder: 'Select Bank Account',
		showSearch: true,
	},
	required,
	type: 'select' as const,
	options: [
		{
			label: 'No bank account',
			value: null,
		},
		{
			label: 'Organization',
			options: [
				...bankAccounts
					.filter((account) => account?.vesselId === null)
					.map((account) => ({
						label: `${account.name || UnnamedBankAccountName} (${account.currency}) (${account.accountNo})`,
						value: account.id,
						disabled: fixture.fixed && account.currency !== fixture.bankAccount?.currency,
					}))],
		},
		{
			label: 'Vessel',
			options: [
				...bankAccounts
					.filter((account) => account?.vesselId != null)
					.map((account) => {
						const label = account.name || UnnamedBankAccountName;
						const suffix = account.businessUnit != null ?
							`(${account.businessUnit}) (${account.currency}) ` :
							`(${account.currency})`;

						return {
							label: `${label} ${suffix} (${account.accountNo})`,
							value: account.id,
							disabled: fixture.fixed && account.currency !== fixture.bankAccount?.currency,
						};
					})],
		},
	],
	render: () => {
		const bankAccount = bankAccounts.find((ba) => ba.id === fixture.bankAccountId);
		const additionalBankAccountsTooltip = (
			<TooltipIcon>
				Additional bank accounts can be created on the vessel page.
			</TooltipIcon>
		);

		if (bankAccount == null && bankAccounts.length === 0) {
			return (
				<span>
					No bank account selected.
					{additionalBankAccountsTooltip}
				</span>
			);
		}

		const account = (bankAccount || bankAccounts[0]);

		return (
			<span>
				{`${account.name} (${account.currency}) no. ${account?.accountNo}`}
				{additionalBankAccountsTooltip}
			</span>
		);
	},
});

export const getCpDateField = ({
	fixture,
	required,
}: {
	fixture: SectionItemsFixture;
	required: boolean;
}) => ({
	key: 'cpDate',
	label: 'CP Date UTC',
	editable: true,
	required,
	type: 'date' as const,
	value: fixture.tcTerms.cpDate,
	render: (entry: { value: Moment | null }) => renderUtcDate(entry.value, DATE_AND_TIME),
});

export const getCpFormField = ({
	fixture,
	required,
}: {
	fixture: SectionItemsFixture;
	required: boolean;
}) => ({
	key: 'cpForm',
	label: 'CP Form',
	value: fixture.tcTerms.cpForm,
	editable: true,
	required,
});

export const getLaycanFromField = ({
	fixture,
	firstDeliveryPort,
}: {
	fixture: SectionItemsFixture;
	firstDeliveryPort: FirstDeliveryPort;
}) => ({
	key: 'laycanFrom',
	label: 'Laycan From',
	value: fixture.tcTerms.laycanFrom,
	render: (v: Moment | null) => renderUtcDate(v, DATE_AND_TIME),
	editable: true,
	required: true,
	type: 'date' as const,
	transformData: {
		in: (iso: string) => fromISOTimezone(
			iso,
			TimeZoneFormat.LocalTime,
			firstDeliveryPort?.timezone,
			TimeZoneMode.WallTime,
		),
		out: (val: Moment | null) => (val ?
			toISOTimezone(
				val,
				TimeZoneFormat.LocalTime,
				firstDeliveryPort?.timezone,
				TimeZoneMode.WallTime,
			) :
			null),
	},
	renderInput: ({ value, onChange }: {
		value: Moment | null;
		onChange: (newValue: Moment | null) => void;
	}) => {
		return (
			<Space size="small" align="center">
				<DatePicker
					key={`laycanFrom-${fixture.tcTerms.laycanFrom ?? 'none'}`}
					fullWidth
					value={value}
					allowClear
					className={styles.slimInput}
					time
					showTimezone={false}
					onChange={(picked) => {
						if (!picked || Array.isArray(picked)) {
							onChange(null);

							return;
						}

						onChange(
							fromISOTimezone(
								toMoment(picked).toISOString(true),
								TimeZoneFormat.LocalTime,
								firstDeliveryPort?.timezone,
								TimeZoneMode.WallTime,
							),
						);
					}}
					showNow={false}
					maxDate={fixture.tcTerms.laycanTo ?? undefined}
				/>
				<span style={{ fontSize: 12, opacity: 0.7 }}>
					{timeZoneOffsetLabel(
						value ?? nowMoment(),
						firstDeliveryPort?.timezone ?? null,
						TimeZoneFormat.LocalTime,
					)}
				</span>
			</Space>
		);
	},
	inputProps: {
	},
});

export const getLaycanToField = ({
	fixture,
	firstDeliveryPort,
}: {
	fixture: SectionItemsFixture;
	firstDeliveryPort: FirstDeliveryPort;
}) => ({
	key: 'laycanTo',
	label: 'Laycan To',
	value: fixture.tcTerms.laycanTo,
	render: (v: Moment | null) => renderUtcDate(v, DATE_AND_TIME),
	editable: true,
	required: true,
	type: 'date' as const,
	transformData: {
		in: (iso: string) => fromISOTimezone(
			iso,
			TimeZoneFormat.LocalTime,
			firstDeliveryPort?.timezone,
			TimeZoneMode.WallTime,
		),
		out: (val: Moment | null) => (val ?
			toISOTimezone(
				val,
				TimeZoneFormat.LocalTime,
				firstDeliveryPort?.timezone,
				TimeZoneMode.WallTime,
			) :
			null),
	},
	renderInput: ({ value, onChange }: {
		value: Moment | null;
		onChange: (newValue: Moment | null) => void;
	}) => {
		return (
			<Space size="small" align="center">
				<DatePicker
					key={`laycanTo-${fixture.tcTerms.laycanTo?.toISOString(true) ?? 'none'}`}
					fullWidth
					value={value}
					allowClear
					className={styles.slimInput}
					time
					showTimezone={false}
					onChange={(picked) => {
						if (!picked || Array.isArray(picked)) {
							onChange(null);

							return;
						}

						onChange(
							fromISOTimezone(
								toMoment(picked).toISOString(true),
								TimeZoneFormat.LocalTime,
								firstDeliveryPort?.timezone,
								TimeZoneMode.WallTime,
							),
						);
					}}
					showNow={false}
					minDate={fixture.tcTerms.laycanFrom ?? undefined}
				/>
				<span style={{ fontSize: 12, opacity: 0.7 }}>
					{timeZoneOffsetLabel(
						value ?? nowMoment(),
						firstDeliveryPort?.timezone ?? null,
						TimeZoneFormat.LocalTime,
					)}
				</span>
			</Space>
		);
	},
	inputProps: {
	},
});

export const getAddressCommissionField = ({
	fixture,
	allowEditKeyTerms,
	required,
}: {
	fixture: SectionItemsFixture;
	allowEditKeyTerms: boolean;
	required: boolean;
}) => ({
	key: 'addressCommission',
	label: 'Address Commission',
	type: 'percentage' as const,
	value: fixture.tcTerms.addressCommission,
	render: ({ value }: { value: number | null }) => value != null && `${value}%`,
	editable: allowEditKeyTerms,
	inputProps: {
		'data-testid': 'addressCommission'
	},
	required,
});

export const getNotesField = ({
	fixture,
	required,
}: {
	fixture: SectionItemsFixture;
	required: boolean;
}) => ({
	key: 'note',
	label: 'Note',
	type: 'textarea' as const,
	value: fixture.tcTerms.note,
	editable: true,
	required,
	inputProps: { rows: 5 },
});

export const getDefaultLaycanUtcOffset = (fixture: SectionItemsFixture | undefined) => {
	const deliveryPort = fixture?.tcTerms?.deliveryPortsOrRanges?.[0];

	if (deliveryPort) {
		const firstLaycan = fixture?.tcTerms?.laycanFrom ?? fixture?.tcTerms.laycanTo;
		const dateForOffset = firstLaycan ? toMoment(firstLaycan).toDate() : undefined;
		const portOffset = getUtcOffset(deliveryPort, dateForOffset);

		if (portOffset != null) {
			return portOffset;
		}
	}

	if (fixture?.tcTerms?.laycanFrom) {
		return toMoment(fixture.tcTerms.laycanFrom).utcOffset();
	}

	if (fixture?.tcTerms?.laycanTo) {
		return toMoment(fixture.tcTerms.laycanTo).utcOffset();
	}

	return 0;
};
